<template>
  <div
    v-if="isRendered"
    class="position-dialogs"
    :class="{ 'position-dialogs--open': isShown }"
    role="dialog"
    aria-modal="true"
  >
    <div class="position-dialogs__backdrop" @click.self="close">
      <div
        class="position-dialogs__panel position-dialogs__popup position-dialogs--bg-pattern"
        :style="{ width: '450px' }"
        @click.stop
      >
        <div class="position-dialogs__header">
          <span class="position-dialogs__icon-wrapper">
            <Icon :name="resolvedIcon" />
          </span>

          <div class="position-dialogs__spacer" />

          <button
            type="button"
            class="position-dialogs__close-button"
            aria-label="Close dialog"
            @click="close"
          >
            <Icon class="position-dialogs__cursor-pointer" name="popup_close" />
          </button>
        </div>

        <div class="position-dialogs__content">
          <h3 class="position-dialogs__title" :class="{ 'text-center': center }">
            {{ resolvedTitle }}
          </h3>
          <div
            v-if="resolvedMessageHtml"
            v-html="resolvedMessageHtml"
            class="position-dialogs__message"
            :class="{ 'text-center': center }"
          ></div>
          <p
            v-if="errorMessage"
            class="position-dialogs__error"
            role="alert"
          >
            {{ errorMessage }}
          </p>
        </div>

        <div class="position-dialogs__footer">
          <StandardButton
            :loaderLeft="$loader.isAppLoading"
            size="full-width"
            :type="buttonType"
            :label="actionLabel"
            :disabled="$loader.isAppLoading"
            @click="emit('onAction')"
            class="position-dialogs__button"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const ANIMATION_MS = 180;

const props = defineProps({
  value: {
    type: Boolean,
    default: false
  },
  selfManaged: {
    type: Boolean,
    default: false
  },
  icon: {
    type: String,
    default: ""
  },
  title: {
    type: String,
    default: ""
  },
  message: {
    type: String,
    default: ""
  },
  actionLabel: {
    type: String,
    default: "Activate"
  },
  buttonType: {
    type: String,
    default: "magenta"
  },
  center: {
    type: Boolean,
    default: false
  },
  /** Inline failure copy; the dialog stays open so the admin can retry. */
  errorMessage: {
    type: String,
    default: ""
  }
});

const emit = defineEmits(["input", "onAction", "onClose"]);

// Always keep an internal open state so the dialog can visually close itself
// even if the parent doesn't immediately (or ever) update `value`.
const internalOpen = ref(props.value);
watch(
  () => props.value,
  (val) => {
    internalOpen.value = val;
  },
  { immediate: true }
);

// We keep the dialog mounted briefly on close so CSS can animate out.
const isRendered = ref(props.value);
const isShown = ref(props.value);
let closeTimer = null;

const syncAnimationState = (open) => {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }

  if (open) {
    isRendered.value = true;
    // Next frame so the browser can apply "closed" styles first.
    requestAnimationFrame(() => {
      isShown.value = true;
    });
  } else {
    isShown.value = false;
    closeTimer = setTimeout(() => {
      if (!internalOpen.value) isRendered.value = false;
      closeTimer = null;
    }, ANIMATION_MS);
  }
};

watch(
  internalOpen,
  (open) => {
    syncAnimationState(open);
  },
  { immediate: true }
);

const close = () => {
  internalOpen.value = false;
  // Keep emitting for compatibility with existing v-model/onClose listeners.
  emit("input", false);
  emit("onClose");
};

const onKeydown = (e) => {
  if (e.key === "Escape") close();
};

let previousBodyOverflow = null;
watch(
  isRendered,
  (rendered) => {
    if (rendered) {
      document.addEventListener("keydown", onKeydown);
      previousBodyOverflow = document?.body?.style?.overflow ?? "";
      if (document?.body) document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("keydown", onKeydown);
      if (document?.body && previousBodyOverflow !== null) {
        document.body.style.overflow = previousBodyOverflow;
      }
      previousBodyOverflow = null;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  document.removeEventListener("keydown", onKeydown);
  if (document?.body && previousBodyOverflow !== null) {
    document.body.style.overflow = previousBodyOverflow;
  }
});

const resolvedIcon = computed(() => props.icon || "org_icon_position");
const resolvedTitle = computed(() => props.title || "Activate");
const resolvedMessageHtml = computed(() => {
  if (!props.message) return "";
  return props.message
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
});
</script>
<style scoped>
.position-dialogs {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}

.position-dialogs__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  opacity: 0;
  transition: opacity 180ms ease;
  pointer-events: none;
}

.position-dialogs--open {
  pointer-events: auto;
}

.position-dialogs--open .position-dialogs__backdrop {
  opacity: 1;
  pointer-events: auto;
}

.position-dialogs__panel {
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(16, 24, 40, 0.2);
  transform: translateY(10px) scale(0.98);
  opacity: 0;
  transition: transform 180ms ease, opacity 180ms ease;
  will-change: transform, opacity;
}

.position-dialogs--open .position-dialogs__panel {
  transform: translateY(0) scale(1);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .position-dialogs__backdrop,
  .position-dialogs__panel {
    transition: none !important;
  }
}

.position-dialogs__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.position-dialogs__spacer {
  flex: 1;
}

.position-dialogs__close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #ffffff;
  padding: 0;
  cursor: pointer;
  margin-top: -20px;
}

.position-dialogs__close-button:focus-visible {
  outline: 2px solid rgba(127, 86, 217, 0.6);
  outline-offset: 2px;
}

.position-dialogs__content {
  margin-bottom: 32px;
}

.position-dialogs__footer {
  display: flex;
}

.position-dialogs__popup {
  border-radius: 12px;
}
.position-dialogs__popup .position-dialogs__icon-wrapper {
  border: 1px solid #EAECF0;
  padding: 12px;
  border-radius: 10px;
}
.position-dialogs__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
  margin: 0 0 20px 0;
}
.position-dialogs__message {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #666666;
  text-align: left !important;
}
.position-dialogs__message.text-center {
  text-align: center !important;
}
.position-dialogs__error {
  margin: 12px 0 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #b42318;
  text-align: left;
}
.position-dialogs__title.text-center {
  text-align: center !important;
}
.position-dialogs__avatar-text {
  color: #7f56d9;
  line-height: 1.5rem;
  font-weight: 500;
}
.position-dialogs--bg-pattern {
  background-image: url("@components/assets/backgrounds/position_background_pattern_decorative.svg");
  background-size: 216px 216px;
  background-position: 1px 1px;
  background-repeat: no-repeat;
}
.position-dialogs__cursor-pointer {
  cursor: pointer;
}
.position-dialogs__button-disabled {
  background-color: #E0E0E0 !important;
}
.position-dialogs__button.standard-button--full-width {
  height: 40px;
}
</style>